// src/pages/profile/ChangePassword.tsx
import React, { useState, useEffect } from 'react';
import { Lock, Eye, EyeOff, Save, Shield, CheckCircle, XCircle, Info } from 'lucide-react';
import { Button, Card, Breadcrumb, Input } from '../../components/ui';
import { authService } from '../../services/auth';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import toast from 'react-hot-toast';

const ChangePassword: React.FC = () => {
  const settings = useSystemSettingsStore(state => state.settings);
  const fetchPublicSettings = useSystemSettingsStore(state => state.fetchPublicSettings);
  const [saving, setSaving] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load settings
  useEffect(() => {
    fetchPublicSettings();
  }, [fetchPublicSettings]);

  // Dynamic password rules dari System Settings
  const minLength = Number(settings.password_min_length) || 8;
  const requireUppercase = settings.password_require_uppercase === true;
  const requireNumber = settings.password_require_number === true;
  const requireSpecial = settings.password_require_special === true;

  // Password rules list (dynamic)
  const getPasswordRules = () => {
    const rules = [
      {
        key: 'length',
        label: `Minimal ${minLength} karakter`,
        met: formData.new_password.length >= minLength,
        required: true,
      },
      {
        key: 'uppercase',
        label: 'Mengandung huruf besar (A-Z)',
        met: /[A-Z]/.test(formData.new_password),
        required: requireUppercase,
      },
      {
        key: 'lowercase',
        label: 'Mengandung huruf kecil (a-z)',
        met: /[a-z]/.test(formData.new_password),
        required: true, // Always required
      },
      {
        key: 'number',
        label: 'Mengandung angka (0-9)',
        met: /[0-9]/.test(formData.new_password),
        required: requireNumber,
      },
      {
        key: 'special',
        label: 'Mengandung karakter spesial (!@#$%^&*)',
        met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.new_password),
        required: requireSpecial,
      },
    ];
    return rules;
  };

  // Validate password
  const validatePassword = (password: string): string[] => {
    const errs: string[] = [];
    if (password.length < minLength) {
      errs.push(`Minimal ${minLength} karakter`);
    }
    if (requireUppercase && !/[A-Z]/.test(password)) {
      errs.push('Harus mengandung huruf besar (A-Z)');
    }
    if (requireNumber && !/[0-9]/.test(password)) {
      errs.push('Harus mengandung angka (0-9)');
    }
    if (requireSpecial && !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errs.push('Harus mengandung karakter spesial (!@#$%^&*)');
    }
    return errs;
  };

  // Password strength (dynamic based on settings)
  const getStrength = () => {
    if (!formData.new_password) return { score: 0, label: '', color: '' };

    const rules = getPasswordRules();
    const requiredRules = rules.filter(r => r.required);
    const allRules = rules;

    const requiredMet = requiredRules.filter(r => r.met).length;
    const totalMet = allRules.filter(r => r.met).length;
    const requiredTotal = requiredRules.length;

    // Score based on met rules
    const percent = Math.round((totalMet / allRules.length) * 100);
    const allRequiredMet = requiredMet === requiredTotal;

    if (!allRequiredMet) {
      if (percent <= 20) return { score: percent, label: 'Sangat Lemah', color: 'bg-danger' };
      return { score: percent, label: 'Lemah', color: 'bg-orange-400' };
    }

    if (percent <= 60) return { score: percent, label: 'Cukup', color: 'bg-warning' };
    if (percent <= 80) return { score: percent, label: 'Kuat', color: 'bg-teal-light' };
    return { score: percent, label: 'Sangat Kuat', color: 'bg-success' };
  };

  const strength = getStrength();

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};

    if (!formData.old_password) {
      newErrors.old_password = 'Password lama wajib diisi';
    }
    if (!formData.new_password) {
      newErrors.new_password = 'Password baru wajib diisi';
    }

    // Validate with dynamic rules
    if (formData.new_password) {
      const pwdErrors = validatePassword(formData.new_password);
      if (pwdErrors.length > 0) {
        newErrors.new_password = pwdErrors[0];
      }
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Konfirmasi password wajib diisi';
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = 'Password tidak cocok';
    }

    // Same as old password check
    if (formData.new_password && formData.old_password && formData.new_password === formData.old_password) {
      newErrors.new_password = 'Password baru tidak boleh sama dengan password lama';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      await authService.changePassword({
        current_password: formData.old_password,
        password: formData.new_password,
        password_confirmation: formData.confirm_password,
      });
      toast.success('Password berhasil diubah');
      setFormData({ old_password: '', new_password: '', confirm_password: '' });
      setErrors({});
    } catch (e: any) {
      if (e.response?.status === 422) {
        const backendErrors = e.response.data.errors;
        const mappedErrors: Record<string, string> = {};
        if (backendErrors?.current_password) mappedErrors.old_password = backendErrors.current_password[0];
        if (backendErrors?.password) mappedErrors.new_password = backendErrors.password[0];
        setErrors(mappedErrors);
        toast.error('Gagal mengubah password. Periksa input Anda.');
      } else {
        toast.error(e.response?.data?.message || 'Terjadi kesalahan saat mengubah password');
      }
    } finally {
      setSaving(false);
    }
  };

  const rules = getPasswordRules();

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Profil', href: '/profile' },
        { label: 'Ubah Password' },
      ]} />

      <h1 className="text-2xl font-black text-teal-dark tracking-tight">Ubah Password</h1>

      <div className="max-w-lg">
        <Card>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-teal-main/10 rounded-xl">
              <Shield className="w-6 h-6 text-teal-main" />
            </div>
            <div>
              <h3 className="text-base font-bold text-neutral-text-primary">Keamanan Akun</h3>
              <p className="text-xs text-neutral-text-secondary">Pastikan password kuat dan unik</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Old Password */}
            <div className="relative">
              <Input
                label="Password Lama"
                type={showOld ? 'text' : 'password'}
                value={formData.old_password}
                onChange={(e) => { setFormData({ ...formData, old_password: e.target.value }); setErrors({}); }}
                error={errors.old_password}
                icon={<Lock className="w-4 h-4" />}
                required
              />
              <button
                type="button"
                onClick={() => setShowOld(!showOld)}
                className="absolute right-3 top-9 text-neutral-text-secondary hover:text-neutral-text-primary"
              >
                {showOld ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* New Password */}
            <div className="relative">
              <Input
                label="Password Baru"
                type={showNew ? 'text' : 'password'}
                placeholder={`Minimal ${minLength} karakter`}
                value={formData.new_password}
                onChange={(e) => { setFormData({ ...formData, new_password: e.target.value }); setErrors({}); }}
                error={errors.new_password}
                icon={<Lock className="w-4 h-4" />}
                required
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-9 text-neutral-text-secondary hover:text-neutral-text-primary"
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Dynamic Password Policy Checklist */}
            {formData.new_password && (
              <div className="space-y-3">
                {/* Strength Bar */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-neutral-text-secondary uppercase font-semibold">Kekuatan Password</span>
                    <span className={`text-xs font-bold ${strength.score <= 40 ? 'text-danger' :
                        strength.score <= 60 ? 'text-warning' :
                          strength.score <= 80 ? 'text-info' :
                            'text-success'
                      }`}>
                      {strength.label}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-neutral-border rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${strength.color}`}
                      style={{ width: `${strength.score}%` }}
                    />
                  </div>
                </div>

                {/* Rules Checklist */}
                <div className="p-3 bg-neutral-bg rounded-xl border border-neutral-border space-y-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">
                    Kebijakan Password
                  </p>
                  {rules.map((rule) => (
                    <div key={rule.key} className="flex items-center gap-2">
                      {rule.met ? (
                        <CheckCircle className="w-4 h-4 text-success shrink-0" />
                      ) : (
                        <XCircle className={`w-4 h-4 shrink-0 ${rule.required ? 'text-danger' : 'text-neutral-border'}`} />
                      )}
                      <span className={`text-xs ${rule.met
                          ? 'text-success'
                          : rule.required
                            ? 'text-danger font-medium'
                            : 'text-neutral-text-secondary'
                        }`}>
                        {rule.label}
                        {!rule.required && (
                          <span className="text-neutral-text-secondary/50 ml-1 font-normal">(opsional)</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirm Password */}
            <div className="relative">
              <Input
                label="Konfirmasi Password Baru"
                type={showConfirm ? 'text' : 'password'}
                value={formData.confirm_password}
                onChange={(e) => { setFormData({ ...formData, confirm_password: e.target.value }); setErrors({}); }}
                error={errors.confirm_password}
                icon={<Lock className="w-4 h-4" />}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-9 text-neutral-text-secondary hover:text-neutral-text-primary"
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>

              {/* Match indicator */}
              {formData.confirm_password && (
                <div className={`flex items-center gap-1.5 mt-1 text-xs ${formData.new_password === formData.confirm_password ? 'text-success' : 'text-danger'
                  }`}>
                  {formData.new_password === formData.confirm_password ? (
                    <><CheckCircle className="w-3.5 h-3.5" /> Password cocok</>
                  ) : (
                    <><XCircle className="w-3.5 h-3.5" /> Password tidak cocok</>
                  )}
                </div>
              )}
            </div>

            {/* Info about policy source */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-xl border border-blue-200">
              <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700">
                Kebijakan password diatur oleh administrator melalui Pengaturan Sistem.
                {!requireUppercase && !requireNumber && !requireSpecial && (
                  <span className="block mt-1 text-blue-600">
                    Saat ini hanya minimal {minLength} karakter yang diwajibkan.
                  </span>
                )}
              </p>
            </div>

            <div className="pt-4">
              <Button
                variant="primary"
                className="w-full"
                onClick={handleSubmit}
                loading={saving}
                icon={<Save className="w-4 h-4" />}
              >
                Ubah Password
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ChangePassword;
